/**
 * @fileoverview Full-text search functionality with progress callbacks
 */

import { EventEmitter } from 'eventemitter3'
import { SandboxFileSystem } from './filesystem'

/**
 * Search options
 */
export interface SearchOptions {
  /** Search query (string or regex) */
  query: string | RegExp
  /** File patterns to include */
  include?: string[]
  /** File patterns to exclude */
  exclude?: string[]
  /** Case sensitive search */
  caseSensitive?: boolean
  /** Use regex for search */
  useRegex?: boolean
  /** Maximum file size to search (bytes) */
  maxFileSize?: number
  /** Search in binary files */
  searchBinary?: boolean
  /** Maximum results to return */
  maxResults?: number
  /** Search directory (default: /) */
  directory?: string
  /** Recursive search */
  recursive?: boolean
}

/**
 * Search result
 */
export interface SearchResult {
  /** File path */
  file: string
  /** Line number (1-based) */
  line: number
  /** Column number (1-based) */
  column: number
  /** Matched text */
  match: string
  /** Full line content */
  context: string
  /** Match start position in context */
  matchStart: number
  /** Match end position in context */
  matchEnd: number
}

/**
 * Search progress information
 */
export interface SearchProgress {
  /** Current file being searched */
  currentFile: string
  /** Files processed */
  filesProcessed: number
  /** Total files to process */
  totalFiles: number
  /** Results found so far */
  resultsFound: number
  /** Progress percentage (0-100) */
  progress: number
  /** Estimated time remaining (ms) */
  estimatedTimeRemaining?: number
  /** Search speed (files/second) */
  searchSpeed: number
}

/**
 * Search summary
 */
export interface SearchSummary {
  /** Total files searched */
  filesSearched: number
  /** Total results found */
  totalResults: number
  /** Search duration (ms) */
  duration: number
  /** Files with matches */
  filesWithMatches: number
  /** Average search speed (files/second) */
  averageSpeed: number
  /** Cancelled by user */
  cancelled: boolean
  /** Completed successfully */
  completed: boolean
}

/**
 * Full-text search engine
 */
export class SearchEngine extends EventEmitter {
  private fs: SandboxFileSystem
  private cancelled = false
  private searchId = 0

  constructor(fs: SandboxFileSystem) {
    super()
    this.fs = fs
  }

  /**
   * Perform full-text search
   */
  async search(
    options: SearchOptions,
    onProgress?: (progress: SearchProgress) => void
  ): Promise<{results: SearchResult[], summary: SearchSummary}> {
    const searchId = ++this.searchId
    this.cancelled = false

    const startTime = Date.now()
    const results: SearchResult[] = []
    const filesWithMatches = new Set<string>()

    // Prepare search query
    const searchRegex = this.createSearchRegex(options)

    // Get files to search
    const filesToSearch = await this.getFilesToSearch(options)
    const totalFiles = filesToSearch.length

    let filesProcessed = 0

    for (const filePath of filesToSearch) {
      if (this.cancelled || this.searchId !== searchId) {
        break
      }

      try {
        const fileResults = await this.searchFile(filePath, searchRegex, options)
        results.push(...fileResults)

        if (fileResults.length > 0) {
          filesWithMatches.add(filePath)
        }

        filesProcessed++

        // Report progress
        const progress: SearchProgress = {
          currentFile: filePath,
          filesProcessed,
          totalFiles,
          resultsFound: results.length,
          progress: Math.round((filesProcessed / totalFiles) * 100),
          searchSpeed: filesProcessed / ((Date.now() - startTime) / 1000),
          estimatedTimeRemaining: filesProcessed > 0 ?
            Math.round(((totalFiles - filesProcessed) / filesProcessed) * (Date.now() - startTime)) :
            undefined
        }

        if (onProgress) {
          onProgress(progress)
        }

        this.emit('progress', progress)

        // Check if we've hit max results
        if (options.maxResults && results.length >= options.maxResults) {
          results.splice(options.maxResults)
          break
        }

      } catch (error) {
        this.emit('error', { file: filePath, error })
      }
    }

    const summary: SearchSummary = {
      filesSearched: filesProcessed,
      totalResults: results.length,
      duration: Date.now() - startTime,
      filesWithMatches: filesWithMatches.size,
      averageSpeed: filesProcessed / ((Date.now() - startTime) / 1000),
      cancelled: this.cancelled,
      completed: !this.cancelled && filesProcessed === totalFiles
    }

    this.emit('complete', { results, summary })

    return { results, summary }
  }

  /**
   * Search within a specific file
   */
  async searchFile(
    filePath: string,
    regex: RegExp,
    options: SearchOptions
  ): Promise<SearchResult[]> {
    const results: SearchResult[] = []

    try {
      // Check file size
      const stats = await this.fs.stat(filePath)
      if (options.maxFileSize && stats.size > options.maxFileSize) {
        return results
      }

      // Skip binary files unless requested
      if (!options.searchBinary && this.isBinaryFile(filePath)) {
        return results
      }

      // Read file content
      const content = await this.fs.readFile(filePath) as string
      if (typeof content !== 'string') {
        return results
      }

      // Split into lines for line-based searching
      const lines = content.split('\n')

      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex]
        let match

        // Reset regex for global search
        regex.lastIndex = 0

        while ((match = regex.exec(line)) !== null) {
          results.push({
            file: filePath,
            line: lineIndex + 1,
            column: match.index + 1,
            match: match[0],
            context: line,
            matchStart: match.index,
            matchEnd: match.index + match[0].length
          })

          // Prevent infinite loop on global regex
          if (!regex.global) {
            break
          }
        }
      }

    } catch (error) {
      // Skip files that can't be read
    }

    return results
  }

  /**
   * Cancel current search
   */
  cancel(): void {
    this.cancelled = true
    this.emit('cancelled')
  }

  /**
   * Search and replace functionality
   */
  async searchAndReplace(
    searchOptions: SearchOptions,
    replaceText: string,
    onProgress?: (progress: SearchProgress) => void
  ): Promise<{
    results: SearchResult[],
    replacements: number,
    filesModified: string[],
    summary: SearchSummary
  }> {
    const { results } = await this.search(searchOptions, onProgress)

    const filesModified: string[] = []
    let replacements = 0

    // Group results by file
    const resultsByFile = new Map<string, SearchResult[]>()
    for (const result of results) {
      if (!resultsByFile.has(result.file)) {
        resultsByFile.set(result.file, [])
      }
      resultsByFile.get(result.file)!.push(result)
    }

    // Process each file
    for (const [filePath, fileResults] of resultsByFile) {
      try {
        const content = await this.fs.readFile(filePath) as string
        if (typeof content !== 'string') continue

        const lines = content.split('\n')
        let modified = false

        // Sort results by line number (descending) to maintain positions
        fileResults.sort((a, b) => b.line - a.line || b.column - a.column)

        for (const result of fileResults) {
          const lineIndex = result.line - 1
          if (lineIndex >= 0 && lineIndex < lines.length) {
            const line = lines[lineIndex]
            const before = line.substring(0, result.matchStart)
            const after = line.substring(result.matchEnd)
            lines[lineIndex] = before + replaceText + after
            modified = true
            replacements++
          }
        }

        if (modified) {
          await this.fs.writeFile(filePath, lines.join('\n'))
          filesModified.push(filePath)
        }

      } catch (error) {
        this.emit('error', { file: filePath, error })
      }
    }

    return {
      results,
      replacements,
      filesModified,
      summary: {
        filesSearched: resultsByFile.size,
        totalResults: results.length,
        duration: 0, // Already calculated in search
        filesWithMatches: resultsByFile.size,
        averageSpeed: 0,
        cancelled: false,
        completed: true
      }
    }
  }

  /**
   * Create search regex from options
   */
  private createSearchRegex(options: SearchOptions): RegExp {
    let pattern: string
    let flags = 'g' // Always use global flag for finding all matches

    if (options.query instanceof RegExp) {
      pattern = options.query.source
      flags += options.query.flags.replace(/g/g, '') // Remove existing global flag
    } else {
      // Escape special regex characters if not using regex mode
      pattern = options.useRegex ? options.query : this.escapeRegex(options.query)
    }

    // Add case insensitive flag if needed
    if (!options.caseSensitive) {
      flags += 'i'
    }

    return new RegExp(pattern, flags)
  }

  /**
   * Get list of files to search
   */
  private async getFilesToSearch(options: SearchOptions): Promise<string[]> {
    const files: string[] = []
    const startDir = options.directory || '/'

    const collectFiles = async (dir: string): Promise<void> => {
      try {
        const entries = await this.fs.readdir(dir, { withFileTypes: true })

        for (const entry of entries) {
          const entryName = typeof entry === 'string' ? entry : entry.name
          const fullPath = dir === '/' ? `/${entryName}` : `${dir}/${entryName}`

          if (typeof entry === 'string') {
            // We don't know if it's a file or directory from just the name
            // Skip for now - this should use withFileTypes: true
            continue
          }

          if (entry.isDirectory()) {
            if (options.recursive !== false) {
              await collectFiles(fullPath)
            }
          } else if (entry.isFile()) {
            // Apply include/exclude filters
            if (this.shouldIncludeFile(fullPath, options)) {
              files.push(fullPath)
            }
          }
        }
      } catch (error) {
        // Skip directories that can't be read
      }
    }

    await collectFiles(startDir)
    return files
  }

  /**
   * Check if file should be included in search
   */
  private shouldIncludeFile(filePath: string, options: SearchOptions): boolean {
    const fileName = filePath.split('/').pop() || ''

    // Check include patterns
    if (options.include && options.include.length > 0) {
      if (!options.include.some(pattern => this.matchGlob(fileName, pattern))) {
        return false
      }
    }

    // Check exclude patterns
    if (options.exclude && options.exclude.length > 0) {
      if (options.exclude.some(pattern => this.matchGlob(fileName, pattern))) {
        return false
      }
    }

    return true
  }

  /**
   * Simple glob pattern matching
   */
  private matchGlob(filename: string, pattern: string): boolean {
    const regexPattern = pattern
      .replace(/\./g, '\\.')
      .replace(/\*/g, '.*')
      .replace(/\?/g, '.')

    return new RegExp(`^${regexPattern}$`, 'i').test(filename)
  }

  /**
   * Check if file is binary based on extension
   */
  private isBinaryFile(filePath: string): boolean {
    const binaryExtensions = [
      '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.ico', '.svg',
      '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
      '.zip', '.tar', '.gz', '.rar', '.7z',
      '.mp3', '.mp4', '.avi', '.mov', '.wav',
      '.exe', '.dll', '.so', '.dylib',
      '.bin', '.dat', '.db', '.sqlite'
    ]

    const extension = filePath.toLowerCase().split('.').pop()
    return extension ? binaryExtensions.includes(`.${extension}`) : false
  }

  /**
   * Escape regex special characters
   */
  private escapeRegex(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }
}